import { useCallback, useMemo } from "react";
import { Input } from "../common/input";
import { Select } from "./select";
import { PlaceAutocomplete } from "./place-autocomplete";
import { useCountries } from "../../modules/products/hooks/useCountries";
import { useStates } from "../../modules/products/hooks/useStates";
import type { ParsedAddress } from "../../utils/address-parser";
import { MAX_LENGTH } from "../../utils/validation";

export interface AddressFieldsValue {
  street: string;
  street2: string;
  city: string;
  zip: string;
  countryId: string;
  stateId: string;
}

export const EMPTY_ADDRESS_FIELDS: AddressFieldsValue = {
  street: "",
  street2: "",
  city: "",
  zip: "",
  countryId: "",
  stateId: "",
};

export interface AddressFieldsErrors {
  street?: string;
  street2?: string;
  city?: string;
  zip?: string;
  countryId?: string;
  stateId?: string;
}

export interface AddressFieldsProps {
  value: AddressFieldsValue;
  onChange: (next: AddressFieldsValue) => void;
  showAutocomplete?: boolean;
  required?: {
    street?: boolean;
    city?: boolean;
    zip?: boolean;
    countryId?: boolean;
    stateId?: boolean;
  };
  errors?: AddressFieldsErrors;
  disabled?: boolean;
}

/**
 * Hook that returns a resolver mapping a Google-Places parsed address to the
 * id-based fields used by `AddressFieldsValue`. Country / state matching is
 * done case-insensitively against the platform's master country/state lists.
 *
 * Exposed as a hook (not a pure function) because it needs access to the
 * SWR-cached country / state lookups.
 */
export function useResolveParsedAddress() {
  const { data: countries } = useCountries();
  const { data: allStates } = useStates();

  return useCallback(
    (parsed: ParsedAddress, current: AddressFieldsValue): AddressFieldsValue => {
      const normalize = (v: string) => v.trim().toLowerCase();
      const street = [parsed.streetNumber, parsed.route]
        .filter(Boolean)
        .join(" ")
        .trim();

      const countryMatch = countries?.find(
        (c) => normalize(c.name) === normalize(parsed.country),
      );
      const stateMatch = allStates?.find((s) => {
        const matchesName = normalize(s.name) === normalize(parsed.state);
        const matchesCountry =
          !countryMatch || s.country?.id === countryMatch.id;
        return matchesName && matchesCountry;
      });

      return {
        ...current,
        street: street || current.street,
        city: parsed.locality || current.city,
        zip: parsed.postalCode || current.zip,
        countryId: countryMatch ? String(countryMatch.id) : current.countryId,
        stateId: stateMatch ? String(stateMatch.id) : current.stateId,
      };
    },
    [countries, allStates],
  );
}

/**
 * Postal address sub-form shared by every place in the app that captures a
 * structured address (checkout delivery address, company profile, future
 * billing forms, etc).
 *
 * Owns: place autocomplete, the six address fields, and country→state
 * cascading. Owns no submit logic — the parent decides what to do with the
 * value on save.
 */
export function AddressFields({
  value,
  onChange,
  showAutocomplete = true,
  required,
  errors,
  disabled = false,
}: AddressFieldsProps) {
  const { data: countries, isLoading: countriesLoading } = useCountries();
  const { data: allStates, isLoading: statesLoading } = useStates();
  const resolveParsedAddress = useResolveParsedAddress();

  const countryOptions = useMemo(
    () =>
      (countries ?? []).map((c) => ({ value: String(c.id), label: c.name })),
    [countries],
  );

  const stateOptions = useMemo(() => {
    if (!value.countryId) return [];
    const countryIdNum = Number(value.countryId);
    return (allStates ?? [])
      .filter((s) => s.country?.id === countryIdNum)
      .map((s) => ({ value: String(s.id), label: s.name }));
  }, [allStates, value.countryId]);

  const set = (patch: Partial<AddressFieldsValue>) =>
    onChange({ ...value, ...patch });

  return (
    <div className="flex flex-col gap-4">
      {showAutocomplete && (
        <PlaceAutocomplete
          label="Autofill address"
          placeholder="Start typing a street address"
          disabled={disabled}
          onPlaceSelect={(parsed) => onChange(resolveParsedAddress(parsed, value))}
        />
      )}

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Street"
          placeholder="Street name"
          value={value.street}
          maxLength={MAX_LENGTH.addressLine}
          onChange={(e) => set({ street: e.target.value })}
          required={required?.street}
          error={errors?.street}
          disabled={disabled}
        />
        <Input
          label="Building / Apt / Zone"
          placeholder="Building, apartment, zone…"
          value={value.street2}
          maxLength={MAX_LENGTH.addressLine}
          onChange={(e) => set({ street2: e.target.value })}
          error={errors?.street2}
          disabled={disabled}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="City"
          placeholder="City"
          value={value.city}
          maxLength={MAX_LENGTH.city}
          onChange={(e) => set({ city: e.target.value })}
          required={required?.city}
          error={errors?.city}
          disabled={disabled}
        />
        <Input
          label="Postal code"
          placeholder="Postal code"
          value={value.zip}
          maxLength={MAX_LENGTH.postalCode}
          onChange={(e) => set({ zip: e.target.value })}
          required={required?.zip}
          error={errors?.zip}
          disabled={disabled}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Select
          name="country"
          label="Country"
          placeholder="Select country"
          value={value.countryId}
          onChange={(e) => set({ countryId: e.target.value, stateId: "" })}
          options={countryOptions}
          required={required?.countryId}
          disabled={disabled || countriesLoading}
          error={errors?.countryId}
        />
        <Select
          name="state"
          label="State / Region"
          placeholder={value.countryId ? "Select state" : "Pick a country first"}
          value={value.stateId}
          onChange={(e) => set({ stateId: e.target.value })}
          options={stateOptions}
          required={required?.stateId}
          disabled={
            disabled || statesLoading || !value.countryId || stateOptions.length === 0
          }
          error={errors?.stateId}
        />
      </div>
    </div>
  );
}
